import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentApi } from '../services/api';

/**
 * Shared book card rendered on Kütüphanem, Dashboard and anywhere else
 * that needs to show an activated book. The DB payload shape is whatever
 * `/student/library` returns, so this card is a thin presentation layer on
 * top of that — any field that isn't present (progress, accuracy, cover)
 * degrades gracefully.
 */
export interface BookCardData {
  id: number | string;
  title?: string;
  display_name?: string;
  cover_url?: string | null;
  cover_image_url?: string | null;
  subject?: { name?: string } | null;
  grade_levels?: { grade_number?: number | null; name?: string | null } | null;
  publishers?: { name?: string | null } | null;
  test_count_total?: number;
  test_count_completed?: number;
  progress_pct?: number;
  accuracy_pct?: number | null;
}

interface Props {
  book: BookCardData;
  index?: number;          // optional — picks one of the 5 bundled covers when no cover_url
  variant?: 'full' | 'compact';
  onClick?: (book: BookCardData) => void;
}

function fallbackCover(book: BookCardData, index = 0): string {
  return book.cover_url ?? book.cover_image_url ?? `/a-book${((Number(book.id ?? index) % 5) || 5)}.png`;
}

function gradeLabel(book: BookCardData): string {
  const n = book.grade_levels?.grade_number;
  if (n) return `${n}. Sınıf`;
  const raw = book.grade_levels?.name;
  if (!raw) return '';
  const m = String(raw).match(/^Grade\s+(\d+)/i);
  return m ? `${m[1]}. Sınıf` : String(raw);
}

const BookCard: React.FC<Props> = ({ book, index = 0, variant = 'full', onClick }) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (onClick) onClick(book);
    else navigate('/student/kitap-icerik', { state: { bookId: Number(book.id) } });
  };

  // Phase 3 perf split: /student/library no longer runs S3 LIST per book to
  // fill in totals/progress. When the eager payload didn't include them
  // (test_count_total === 0 / undefined), fetch them lazily once per card so
  // the visible stat catches up to reality. The home grid stays fast (one
  // request for the activations list) and individual cards self-populate.
  const [lazyProgress, setLazyProgress] = useState<{
    test_count_total: number;
    test_count_completed: number;
    progress_pct: number;
    accuracy_pct: number | null;
  } | null>(null);

  useEffect(() => {
    if (book.test_count_total && book.test_count_total > 0) return;
    let cancelled = false;
    studentApi.bookProgress(Number(book.id))
      .then((r) => { if (!cancelled) setLazyProgress(r.data) })
      .catch(() => { /* leave the placeholder zeros */ });
    return () => { cancelled = true; };
  }, [book.id, book.test_count_total]);

  const label = book.display_name ?? book.title ?? 'Kitap';
  const cover = fallbackCover(book, index);
  const total = book.test_count_total && book.test_count_total > 0 ? book.test_count_total : lazyProgress?.test_count_total ?? 0;
  const done = (book.test_count_total && book.test_count_total > 0)
    ? (book.test_count_completed ?? 0)
    : lazyProgress?.test_count_completed ?? 0;
  const progress = Math.min(100, total > 0
    ? Math.round((done / total) * 100)
    : (book.progress_pct ?? 0));
  const accuracyPct = book.accuracy_pct ?? lazyProgress?.accuracy_pct ?? null;

  if (variant === 'compact') {
    return (
      <div
        onClick={handleClick}
        className="bg-[#fafafa] rounded-[16px] p-3 flex gap-3 items-center cursor-pointer hover:shadow-md transition-shadow"
        style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)', minWidth: 220 }}
      >
        <img src={cover} alt="" className="w-[48px] h-[64px] object-cover rounded-[8px] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-[#2b2b2b] truncate">{label}</p>
          {total > 0 && (
            <>
              <p className="text-[11px] text-gray-500 mb-1">{done} / {total} test</p>
              <div className="w-full h-[5px] bg-[#c5c5c5] rounded-full">
                <div className="h-full bg-[#3973bf] rounded-full transition-[width] duration-500" style={{ width: `${progress}%` }} />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      className="bg-[#fafafa] rounded-[24px] p-4 lg:p-5 flex gap-5 w-full max-w-[600px] cursor-pointer hover:shadow-md transition-shadow border border-gray-100"
    >
      <div className="w-[130px] h-[160px] rounded-[12px] overflow-hidden flex-shrink-0">
        <img src={cover} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1">
        <p className="text-[16px] lg:text-[20px] font-medium text-[#2b2b2b] mb-1">{label}</p>
        {book.subject?.name && (
          <p className="text-sm text-[#3973bf] font-medium mb-3">{book.subject.name}</p>
        )}
        {gradeLabel(book) && (
          <p className="text-sm text-gray-500 mb-3">{gradeLabel(book)}</p>
        )}
        {/* Progress only makes sense for books with gradable tests. DAF/föy/
            Deneme (test_count_total === 0) carry no questions, so the İlerleme
            heading + progress bar + Doğruluk are hidden and only a "Yalnızca
            PDF" badge is shown — no misleading empty 0/0 bar. */}
        {total > 0 ? (
          <div className="mb-3">
            <div className="flex justify-between items-center text-[14px] lg:text-[16px] font-medium text-[#2b2b2b] mb-1">
              <span>İlerleme</span>
              <span>{done} / {total} test</span>
            </div>
            <div className="w-full h-[8px] bg-[#c5c5c5] rounded-lg">
              <div className="h-full bg-[#3973bf] rounded-lg transition-[width] duration-500" style={{ width: `${progress}%` }} />
            </div>
            {typeof accuracyPct === 'number' && (
              <p className="text-[12px] text-gray-500 mt-1">Doğruluk: %{accuracyPct}</p>
            )}
          </div>
        ) : (
          <div className="mb-3">
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
              Yalnızca PDF
            </span>
          </div>
        )}
        {book.publishers?.name && (
          <div>
            <p className="text-sm text-gray-500">Yayınevi:</p>
            <p className="text-sm text-[#2b2b2b]">{book.publishers.name}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookCard;
